import { useState, useEffect } from "react";
import { ArrowRight, Calculator } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { roadDistanceKm, isLocalTrip } from "@/lib/geo";

export default function PriceSimulator({ compact = false }) {
  const [data, setData] = useState({
    service_type: "remorquage-accident",
    distance_km: 15,
    vehicle_type: "berline",
    night: false,
    sunday: false,
    accident: false,
    difficult_access: false,
    non_running: false,
    local_zone: false,
  });
  const [quote, setQuote] = useState(null);
  const [coords, setCoords] = useState({ pickup: null, dropoff: null });
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Met à jour la distance + détecte la zone locale (Villepinte) dès que
  // départ et arrivée sont géolocalisés.
  const applyDistance = (pickup, dropoff) => {
    const km = roadDistanceKm(pickup, dropoff);
    const local = isLocalTrip(pickup, dropoff);
    setData((d) => ({ ...d, ...(km != null ? { distance_km: km } : {}), local_zone: local }));
  };
  const onPickupSelect = (f) => {
    const pickup = f ? { lat: f.lat, lng: f.lng } : null;
    setCoords((c) => ({ ...c, pickup }));
    setData((d) => ({ ...d, pickup_address: f ? f.label : d.pickup_address }));
    applyDistance(pickup, coords.dropoff);
  };
  const onDropoffSelect = (f) => {
    const dropoff = f ? { lat: f.lat, lng: f.lng } : null;
    setCoords((c) => ({ ...c, dropoff }));
    setData((d) => ({ ...d, dropoff_address: f ? f.label : d.dropoff_address }));
    applyDistance(coords.pickup, dropoff);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/quote/estimate", data).then((r) => setQuote(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [data]);

  const inputCls = "w-full h-12 px-4 bg-surface-50 border border-surface-200 rounded-xl text-txt-900 text-sm placeholder:text-txt-300 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all";

  return (
    <div className={`${compact ? "p-5" : "p-6 sm:p-8"}`} data-testid="price-simulator">
      {!compact && (
        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <Calculator className="text-brand" size={20} />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-txt-900">Estimez votre prix</h3>
              <p className="text-txt-500 text-sm">Résultat instantané en quelques clics</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        <select className={`${inputCls} appearance-none`} value={data.service_type} onChange={(e) => setField("service_type", e.target.value)} data-testid="sim-service">
          <option value="depannage-panne">Dépannage panne</option>
          <option value="remorquage-accident">Remorquage / accident</option>
          <option value="transport-vehicule">Transport de véhicule</option>
          <option value="sortie-fourriere">Sortie fourrière</option>
          <option value="batterie">Batterie</option>
          <option value="crevaison">Crevaison</option>
        </select>

        <AddressAutocomplete
          value={data.pickup_address || ""}
          onChange={(t) => setField("pickup_address", t)}
          onSelect={onPickupSelect}
          placeholder="Adresse de départ"
          inputClassName={inputCls}
          testid="sim-pickup"
        />
        <AddressAutocomplete
          value={data.dropoff_address || ""}
          onChange={(t) => setField("dropoff_address", t)}
          onSelect={onDropoffSelect}
          placeholder="Adresse d'arrivée"
          inputClassName={inputCls}
          testid="sim-dropoff"
        />

        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-txt-500 w-full sm:w-28 sm:shrink-0">Distance (km)</label>
          <input
            type="range"
            min="0"
            max="200"
            step="1"
            value={data.distance_km}
            onChange={(e) => setField("distance_km", Number(e.target.value))}
            className="flex-1 min-w-0 accent-[#FF6200] h-2"
            data-testid="sim-distance"
          />
          <span className="text-2xl font-extrabold text-txt-900 w-14 text-right">{data.distance_km}</span>
        </div>

        <select className={`${inputCls} appearance-none`} value={data.vehicle_type} onChange={(e) => setField("vehicle_type", e.target.value)} data-testid="sim-vehicle">
          <option value="citadine">Citadine</option>
          <option value="berline">Berline</option>
          <option value="suv">SUV</option>
          <option value="utilitaire">Utilitaire léger</option>
          <option value="hybride">Hybride</option>
          <option value="electrique">Électrique</option>
        </select>

        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Nuit (22h-7h)" checked={data.night} onChange={(v) => setField("night", v)} testid="sim-night" />
          <Toggle label="Dimanche / férié" checked={data.sunday} onChange={(v) => setField("sunday", v)} testid="sim-sunday" />
          <Toggle label="Accident" checked={data.accident} onChange={(v) => setField("accident", v)} testid="sim-accident" />
          <Toggle label="Accès difficile" checked={data.difficult_access} onChange={(v) => setField("difficult_access", v)} testid="sim-access" />
        </div>
      </div>

      <div className="mt-5 bg-ink-900 text-white rounded-xl px-6 py-6 text-center" data-testid="sim-result">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-300">Estimation</div>
        <div className="text-4xl lg:text-5xl font-extrabold mt-2">
          {quote ? formatPrice(quote.estimated_price) : "—"}
          <span className="text-lg font-semibold text-brand-300 ml-1">TTC</span>
        </div>
        {quote?.local_zone && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-brand/15 text-brand-300 text-xs font-semibold rounded-full px-3 py-1" data-testid="sim-local-zone">
            Tarif zone locale — Villepinte & alentours (10 km)
          </div>
        )}
        <div className="text-xs text-brand-300 mt-2">Prix indicatif — Devis final par téléphone</div>
      </div>

      <a
        href="#request-form-anchor"
        onClick={() => trackEvent("click_devis", data.service_type, { label: "simulator_cta" })}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-600 text-white font-bold rounded-xl text-base py-4 transition-colors"
        data-testid="sim-cta-btn"
      >
        Demander un devis gratuit <ArrowRight size={18} />
      </a>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label
      className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl cursor-pointer text-sm transition-all ${
        checked ? "border-brand bg-brand-50 text-brand-700 font-bold" : "border-surface-200 text-txt-500 hover:border-surface-300"
      }`}
      data-testid={testid}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#FF6200] rounded" />
      {label}
    </label>
  );
}
